import { Notify } from 'quasar'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { showErrorNotification, showSuccessNotification } from 'src/CrudManager/Core/Utils/ToastNotification'

export interface DownloadReportOptions {
  url: string
  fallbackFilename?: string
  successMessage?: string
  errorMessage?: string
  progressMessage?: string
}

function mimeFromFilename(name: string): string {
  if (name.endsWith('.xlsx')) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  }
  if (name.endsWith('.pdf')) {
    return 'application/pdf'
  }
  return 'application/octet-stream'
}

const MIN_LOADING_MS = 600

export async function downloadReport(options: DownloadReportOptions): Promise<void> {
  let url: string | null = null
  const startedAt = performance.now()
  const dismissLoading = Notify.create({
    type: 'ongoing',
    message: options.progressMessage ?? 'Generating report…',
    spinner: true,
    position: 'top',
    timeout: 0,
    group: `download:${options.url}`,
  })
  try {
    const response = await axiosApi({
      url: options.url,
      method: 'GET',
      responseType: 'blob',
    })

    const contentDisposition = response.headers['content-disposition']
    let filename = options.fallbackFilename ?? 'report.pdf'
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) {
        filename = match[1].replace(/['"]/g, '').trim()
      }
    }

    const blobType =
      (typeof response.headers['content-type'] === 'string' && response.headers['content-type']) || mimeFromFilename(filename)

    const blob = new Blob([response.data], { type: blobType })
    url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    if (options.successMessage) {
      showSuccessNotification(options.successMessage)
    }
  } catch (error) {
    console.error('Error downloading report:', error)
    showErrorNotification(options.errorMessage ?? 'Download failed', error)
  } finally {
    const elapsed = performance.now() - startedAt
    if (elapsed < MIN_LOADING_MS) {
      await new Promise((resolve) => setTimeout(resolve, MIN_LOADING_MS - elapsed))
    }
    dismissLoading()
    if (url) window.URL.revokeObjectURL(url)
  }
}
